<script setup lang="ts">
/**
 * ImageLightbox — visionneuse plein écran réutilisable (galeries publiques).
 *
 * Autonome : le parent fournit les URLs pleine taille et appelle `open(index)`
 * via une template ref ; l'état (ouvert/index) vit ici.
 *
 * UX modal (Karim 2026-07-11) : backdrop flouté, carte arrondie qui « pop »
 * (scale + fade), transition douce entre les images, boutons blancs solides
 * (pas de fond translucide — règle UI), navigation clavier (Échap / ← / →),
 * verrou du scroll body.
 */
const props = defineProps<{
  /** URLs pleine taille, dans l'ordre d'affichage de la galerie. */
  images: string[];
}>();

const isOpen = ref(false);
const currentIndex = ref(0);

// Chargement de l'image courante : tant qu'elle n'est pas prête, on affiche un
// spinner DANS une carte à taille stable (sinon la carte gris foncé n'a aucune
// dimension → les boutons de nav sautent et chevauchent le bouton Fermer, et on
// voit un vide gris minuscule pendant tout le chargement). Karim 2026-07-23.
const isImageLoading = ref(false);
// Cache des URLs déjà chargées (préchargées ou vues) → pas de spinner si l'image
// est déjà en cache navigateur (navigation instantanée vers une voisine).
const loadedSrcs = ref<Set<string>>(new Set());

function markLoaded() {
  const src = props.images[currentIndex.value];
  if (src) loadedSrcs.value.add(src);
  isImageLoading.value = false;
}

/** Précharge une URL en arrière-plan (image voisine) pour une nav fluide. */
function preload(index: number) {
  if (!import.meta.client) return;
  const src = props.images[index];
  if (!src || loadedSrcs.value.has(src)) return;
  const img = new Image();
  img.onload = () => loadedSrcs.value.add(src);
  img.src = src;
}

// À chaque changement d'image : spinner seulement si pas déjà en cache, puis
// on précharge les voisines (index ±1) pour que prev/next soient instantanés.
watch(currentIndex, (idx) => {
  const src = props.images[idx];
  isImageLoading.value = !!src && !loadedSrcs.value.has(src);
  preload(idx + 1 < props.images.length ? idx + 1 : 0);
  preload(idx - 1 >= 0 ? idx - 1 : props.images.length - 1);
});

function open(index: number) {
  currentIndex.value = Math.min(Math.max(index, 0), props.images.length - 1);
  const src = props.images[currentIndex.value];
  isImageLoading.value = !!src && !loadedSrcs.value.has(src);
  isOpen.value = true;
  // Précharge les voisines dès l'ouverture.
  if (props.images.length > 1) {
    preload(currentIndex.value + 1 < props.images.length ? currentIndex.value + 1 : 0);
    preload(currentIndex.value - 1 >= 0 ? currentIndex.value - 1 : props.images.length - 1);
  }
}

function close() {
  isOpen.value = false;
}

function prev() {
  currentIndex.value =
    currentIndex.value > 0 ? currentIndex.value - 1 : props.images.length - 1;
}

function next() {
  currentIndex.value =
    currentIndex.value < props.images.length - 1 ? currentIndex.value + 1 : 0;
}

defineExpose({ open });

function onKeydown(e: KeyboardEvent) {
  if (!isOpen.value) return;
  if (e.key === "Escape") close();
  else if (e.key === "ArrowLeft") prev();
  else if (e.key === "ArrowRight") next();
}

// Verrou du scroll de la page tant que la lightbox est ouverte — via le
// verrou PARTAGÉ à compteur : la lightbox peut s'ouvrir PAR-DESSUS une autre
// modale (article, listes), l'ancien body.overflow direct déverrouillait tout
// à sa fermeture. Karim 2026-08-05.
useBodyScrollLock().bindToRef(() => isOpen.value);

onMounted(() => window.addEventListener("keydown", onKeydown));
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
});
</script>

<template>
  <Teleport to="body">
    <Transition name="lightbox">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[100] bg-black/70 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
        role="dialog"
        aria-modal="true"
        aria-label="Visionneuse d'images"
        @click.self="close"
      >
        <!-- Carte modale (cible du pop scale+fade via .lightbox-card) -->
        <div class="lightbox-card relative flex flex-col items-center gap-4 max-w-full">
          <!-- Fermer — décalé vers l'extérieur de la carte ; le padding de
               l'overlay (p-4 sm:p-10) garantit qu'il reste dans l'écran. -->
          <button
            type="button"
            class="absolute -top-4 -right-4 sm:-top-5 sm:-right-5 z-10 size-10 rounded-full bg-white text-gray-900 shadow-lg flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
            :aria-label="$t('association.publicPage.orderErrorBanner.fermer')"
            @click="close"
          >
            <UIcon name="i-lucide-x" class="size-5" />
          </button>

          <!-- Image courante (fondu doux entre les images). La carte garde une
               taille MINIMALE stable pendant le chargement pour que les boutons
               de nav ne sautent pas ; un spinner recouvre la zone tant que
               l'image n'est pas prête. -->
          <div class="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/20 bg-gray-900 min-w-[60vw] sm:min-w-[40vw] min-h-[40vh]">
            <Transition name="lightbox-img" mode="out-in">
              <img
                :key="currentIndex"
                :src="images[currentIndex]"
                :alt="$t('partage.galerie.imageNSur', { n: currentIndex + 1, total: images.length })"
                class="block max-w-[90vw] max-h-[78vh] sm:max-w-[80vw] object-contain select-none"
                draggable="false"
                @load="markLoaded"
                @error="markLoaded"
              />
            </Transition>

            <!-- Spinner de chargement (recouvre la carte, disparaît au @load) -->
            <Transition name="lightbox-spin">
              <div
                v-if="isImageLoading"
                class="absolute inset-0 flex items-center justify-center bg-gray-900"
              >
                <UIcon name="i-lucide-loader-circle" class="size-8 text-white/80 animate-spin" />
              </div>
            </Transition>
          </div>

          <!-- Barre de navigation sous l'image -->
          <div v-if="images.length > 1" class="flex items-center gap-4">
            <button
              type="button"
              class="size-11 rounded-full bg-white text-gray-900 shadow-lg flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
              :aria-label="$t('partage.galerie.imagePrecedente')"
              @click="prev"
            >
              <UIcon name="i-lucide-chevron-left" class="size-6" />
            </button>

            <span class="px-4 py-1.5 rounded-full bg-white text-gray-900 text-sm font-semibold shadow-lg tabular-nums">
              {{ currentIndex + 1 }} / {{ images.length }}
            </span>

            <button
              type="button"
              class="size-11 rounded-full bg-white text-gray-900 shadow-lg flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
              :aria-label="$t('partage.galerie.imageSuivante')"
              @click="next"
            >
              <UIcon name="i-lucide-chevron-right" class="size-6" />
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Overlay : fondu. Carte : pop scale 0.92 → 1 avec léger overshoot (spring). */
.lightbox-enter-active {
  transition: opacity 0.25s ease-out;
}
.lightbox-leave-active {
  transition: opacity 0.2s ease-in;
}
.lightbox-enter-from,
.lightbox-leave-to {
  opacity: 0;
}

.lightbox-enter-active .lightbox-card {
  transition: transform 0.3s cubic-bezier(0.34, 1.4, 0.64, 1);
}
.lightbox-leave-active .lightbox-card {
  transition: transform 0.2s ease-in;
}
.lightbox-enter-from .lightbox-card {
  transform: scale(0.92);
}
.lightbox-leave-to .lightbox-card {
  transform: scale(0.96);
}

/* Changement d'image : fondu croisé doux. */
.lightbox-img-enter-active,
.lightbox-img-leave-active {
  transition: opacity 0.18s ease;
}
.lightbox-img-enter-from,
.lightbox-img-leave-to {
  opacity: 0;
}

/* Spinner de chargement : fondu simple (pas de saut). */
.lightbox-spin-enter-active,
.lightbox-spin-leave-active {
  transition: opacity 0.2s ease;
}
.lightbox-spin-enter-from,
.lightbox-spin-leave-to {
  opacity: 0;
}
</style>
